import { useState } from "react";
import { BookmarkSimple } from "@phosphor-icons/react";

function MealSlotSheet({
  showMealSlotSheet,
  closeMealSlotSheet,
  isMobile,
  appTheme,
  mealSheetExpanded,
  isClosingMealSlotSheet,
  mealSheetIsDragging,
  mealSheetDragY,
  handleSheetDragStart,
  handleSheetDragMove,
  handleSheetDragEnd,
  mealSlotSheetTarget,
  plan,
  removeFromPlan,
  setMealSlotSearch,
  mealSlotSearch,
  setShowFavouritesOnly,
  showFavouritesOnly,
  recipes,
  favouriteRecipes,
  mealSlotSelectedRecipe,
  setMealSlotSelectedRecipe,
  openRecipeDetails,
  applyMealToSlot
}) {
  const TAG_LABELS = {
    "high-protein":   "High Protein",
    "quick":          "Quick",
    "budget":         "Budget",
    "under-500-kcal": "Under 500 kcal",
  };

  const [showFilterPopdown, setShowFilterPopdown] = useState(false);
  const [activeSheetFilters, setActiveSheetFilters] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  if (!showMealSlotSheet) return null;

  const sheetFilters = [
    { label: "High Protein",   value: "high-protein" },
    { label: "Quick",          value: "quick" },
    { label: "Budget",         value: "budget" },
    { label: "Under 500 kcal", value: "under-500-kcal" },
  ];

  function toggleSheetFilter(value) {
    setActiveSheetFilters((prev) =>
      prev.includes(value)
        ? prev.filter((f) => f !== value)
        : [...prev, value]
    );
  }

  function resetSheetFilters() {
    setActiveSheetFilters([]);
    setSortField(null);
    setSortDirection(null);
  }

  const filteredRecipes = recipes.filter((recipe) => {
    const matchesSearch = recipe.name
      .toLowerCase()
      .includes(mealSlotSearch.toLowerCase());
    const matchesCategory = mealSlotSheetTarget
      ? recipe.categories.includes(mealSlotSheetTarget.mealType)
      : true;

    if (activeSheetFilters.length === 0) {
      return matchesSearch && matchesCategory;
    }

    const matchesFavourites = activeSheetFilters.includes("favourites")
      ? favouriteRecipes.includes(recipe.name)
      : true;
    const matchesCustom = activeSheetFilters.includes("custom")
      ? recipe.custom === true
      : true;
    const otherFilters = activeSheetFilters.filter(
      (f) => f !== "favourites" && f !== "custom"
    );
    const matchesOther =
      otherFilters.length === 0 ||
      otherFilters.some(
        (f) => recipe.tags.includes(f) || recipe.categories.includes(f)
      );

    return matchesSearch && matchesCategory && matchesFavourites && matchesCustom && matchesOther;
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.45)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 1400,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center"
      }}
      onClick={() => {
        if (showFilterPopdown) {
          setShowFilterPopdown(false);
        } else {
          closeMealSlotSheet();
        }
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: isMobile ? "100%" : "640px",
          height: "100vh",
          borderRadius: "0 0 28px 28px",
          background: appTheme.pageBackground,
          boxShadow: "0 10px 40px rgba(15,23,42,0.18)",
          display: "flex",
          flexDirection: "column",
          animation: isClosingMealSlotSheet
            ? "slideSheetUp 0.22s ease forwards"
            : "slideSheetDown 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
          overflow: "visible",
          transition: mealSheetIsDragging.current
            ? "none"
            : "height 0.38s cubic-bezier(0.22, 1, 0.36, 1), transform 0.38s cubic-bezier(0.22, 1, 0.36, 1)",
          transform: `translateY(${
            mealSheetExpanded
              ? Math.min(0, mealSheetDragY)
              : Math.max(0, mealSheetDragY)
          }px)`,
          position: "relative"
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 18px 10px",
            flexShrink: 0
          }}
        >
          <div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "500",
                color: appTheme.primaryText,
                textTransform: "capitalize",
                fontFamily: appTheme.fontHeading
              }}
            >
              {mealSlotSheetTarget
                ? mealSlotSheetTarget.isProgrammeSwap
                  ? `${mealSlotSheetTarget.day} ${mealSlotSheetTarget.mealType ? mealSlotSheetTarget.mealType.charAt(0).toUpperCase() + mealSlotSheetTarget.mealType.slice(1) : ""}`
                  : `${mealSlotSheetTarget.dateKey ? new Date(mealSlotSheetTarget.dateKey).toLocaleDateString("en-GB", { weekday: "long" }) : ""} ${mealSlotSheetTarget.mealType ? mealSlotSheetTarget.mealType.charAt(0).toUpperCase() + mealSlotSheetTarget.mealType.slice(1) : ""}`
                : "Choose a meal"}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: appTheme.secondaryText,
                marginTop: "2px"
              }}
            >
              {plan[mealSlotSheetTarget?.dateKey]?.[mealSlotSheetTarget?.mealType]
                ? "Swap this meal or remove it"
                : "Pick a recipe for this slot"}
            </div>
          </div>

          <button
            onClick={closeMealSlotSheet}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "999px",
              border: appTheme.pillBorder,
              background: appTheme.pillBackground,
              color: appTheme.primaryText,
              fontSize: "18px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            ×
          </button>
        </div>

        {/* Current meal banner */}
        {mealSlotSheetTarget &&
          plan[mealSlotSheetTarget.dateKey]?.[mealSlotSheetTarget.mealType] && (
            <div
              style={{
                margin: "0 18px 10px",
                padding: "12px 14px",
                borderRadius: "16px",
                background: appTheme.actionLight || "rgba(230,84,76,0.10)",
                border: appTheme.actionBorder || "1px solid rgba(230,84,76,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                flexShrink: 0
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "700",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: appTheme.secondaryText,
                    marginBottom: "3px"
                  }}
                >
                  Current meal
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: appTheme.primaryText
                  }}
                >
                  {plan[mealSlotSheetTarget.dateKey][mealSlotSheetTarget.mealType].name}
                </div>
              </div>
              <button
                onClick={() => {
                  removeFromPlan(
                    mealSlotSheetTarget.dateKey,
                    mealSlotSheetTarget.mealType
                  );
                  closeMealSlotSheet();
                }}
                style={{
                  padding: "8px 14px",
                  borderRadius: "999px",
                  border: appTheme.actionBorder || "1px solid rgba(230,84,76,0.35)",
                  background: appTheme.actionLight || "rgba(230,84,76,0.12)",
                  color: appTheme.actionColor || "#E6544C",
                  fontWeight: "700",
                  fontSize: "13px",
                  cursor: "pointer",
                  flexShrink: 0
                }}
              >
                Remove
              </button>
            </div>
          )}

        {/* Search + filter button */}
        <div
          style={{
            padding: "0 18px 10px",
            flexShrink: 0,
            display: "flex",
            gap: "8px",
            alignItems: "center",
            position: "relative"
          }}
        >
          <input
            value={mealSlotSearch}
            onChange={(e) => setMealSlotSearch(e.target.value)}
            placeholder={`Search ${mealSlotSheetTarget?.mealType || "recipes"}...`}
            style={{
              flex: 1,
              boxSizing: "border-box",
              padding: "12px 16px",
              borderRadius: "999px",
              border: appTheme.accentBorder,
              background: appTheme.inputBackground,
              fontSize: "14px",
              color: appTheme.primaryText,
              outline: "none"
            }}
          />

          {/* Filter icon button */}
          <button
            onClick={() => setShowFilterPopdown((prev) => !prev)}
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "999px",
              border: activeSheetFilters.length > 0 || showFilterPopdown
                ? appTheme.accentBorder
                : appTheme.pillBorder,
              background: activeSheetFilters.length > 0 || showFilterPopdown
                ? appTheme.accentLight
                : appTheme.pillBackground,
              fontSize: "16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "all 0.18s ease",
              position: "relative"
            }}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="8" x2="21" y2="8" />
              <circle cx="8" cy="8" r="3.5" fill="currentColor" stroke="none" />
              <circle cx="8" cy="8" r="1.2" fill="white" stroke="none" />
              <line x1="3" y1="16" x2="21" y2="16" />
              <circle cx="15" cy="16" r="3.5" fill="currentColor" stroke="none" />
              <circle cx="15" cy="16" r="1.2" fill="white" stroke="none" />
            </svg>
            {activeSheetFilters.length > 0 && (
              <div style={{
                position: "absolute",
                top: "6px",
                right: "6px",
                width: "8px",
                height: "8px",
                borderRadius: "999px",
                background: appTheme.accentColor
              }} />
            )}
          </button>

          {/* Filter popdown */}
          {showFilterPopdown && (
            <>
              <div
                onClick={() => setShowFilterPopdown(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 9
                }}
              />
              <div
                onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                zIndex: 10,
                top: "54px",
                right: "18px",
                width: "200px",
                borderRadius: "20px",
                background: appTheme.cardBackground,
                border: appTheme.accentBorder,
                boxShadow: "0 12px 32px rgba(15,23,42,0.16)",
                backdropFilter: "blur(18px) saturate(180%)",
                WebkitBackdropFilter: "blur(18px) saturate(180%)",
                padding: "14px 14px 12px",
                display: "flex",
                flexDirection: "column",
                gap: "10px"
              }}
            >
              {/* Saved + My Recipes toggles */}
              <div style={{ display: "flex", gap: "7px", marginBottom: "2px" }}>
                <button
                  onClick={() => toggleSheetFilter("favourites")}
                  style={{
                    flex: 1,
                    padding: "7px 10px",
                    borderRadius: "10px",
                    border: activeSheetFilters.includes("favourites") ? appTheme.accentBorder : appTheme.pillBorder,
                    background: activeSheetFilters.includes("favourites") ? appTheme.accentColor : appTheme.pillBackground,
                    color: activeSheetFilters.includes("favourites") ? "#ffffff" : appTheme.primaryText,
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                >
                  <BookmarkSimple weight={activeSheetFilters.includes("favourites") ? "fill" : "bold"} size={13} style={{ verticalAlign: "middle", marginRight: "4px" }} /> Saved
                </button>
                <button
                  onClick={() => toggleSheetFilter("custom")}
                  style={{
                    flex: 1,
                    padding: "7px 10px",
                    borderRadius: "10px",
                    border: activeSheetFilters.includes("custom") ? appTheme.accentBorder : appTheme.pillBorder,
                    background: activeSheetFilters.includes("custom") ? appTheme.accentColor : appTheme.pillBackground,
                    color: activeSheetFilters.includes("custom") ? "#ffffff" : appTheme.primaryText,
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                >
                  My Recipes
                </button>
              </div>

              {/* Divider */}
              <div style={{ height: "1px", background: appTheme.cardBorder.replace("1px solid ", ""), margin: "4px 0" }} />

              {/* Category pills */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
                {sheetFilters
                  .filter((f) => f.value !== "favourites" && f.value !== "custom")
                  .map((filter) => {
                    const isActive = activeSheetFilters.includes(filter.value);
                    return (
                      <button
                        key={filter.value}
                        onClick={() => toggleSheetFilter(filter.value)}
                        style={{
                          padding: "6px 11px",
                          borderRadius: "999px",
                          border: isActive ? appTheme.accentBorder : appTheme.pillBorder,
                          background: isActive ? appTheme.accentColor : appTheme.pillBackground,
                          color: isActive ? "#ffffff" : appTheme.primaryText,
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          transition: "all 0.15s ease"
                        }}
                      >
                        {filter.label}
                      </button>
                    );
                  })}
              </div>


              {/* Reset + Apply */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "2px" }}>
                <button
                  onClick={resetSheetFilters}
                  style={{
                    padding: "7px 13px", borderRadius: "999px",
                    border: appTheme.pillBorder, background: appTheme.pillBackground,
                    color: appTheme.secondaryText, fontSize: "12px", fontWeight: "600", cursor: "pointer"
                  }}
                >
                  Reset
                </button>
                {(() => {
                  const sortReady = !sortField || (sortField && sortDirection);
                  const canApply = sortReady;
                  return (
                    <button
                      onClick={() => canApply && setShowFilterPopdown(false)}
                      style={{
                        padding: "7px 13px", borderRadius: "999px",
                        border: canApply ? appTheme.accentBorder : appTheme.pillBorder,
                        background: canApply ? appTheme.accentLight : appTheme.pillBackground,
                        color: canApply ? appTheme.accentColor : appTheme.mutedText,
                        fontSize: "12px", fontWeight: "700",
                        cursor: canApply ? "pointer" : "not-allowed",
                        opacity: canApply ? 1 : 0.5,
                        transition: "all 0.15s ease"
                      }}
                    >
                      Apply
                    </button>
                  );
                })()}
              </div>
            </div>
            </>
          )}
        </div>

        {/* Recipe list */}
        <div
          className="modal-scroll-hidden"
          style={{
            overflowY: "auto",
            flex: 1,
            padding: "0 18px 20px"
          }}
        >
          {filteredRecipes
            .sort((a, b) => {
      const currentMealName =
        mealSlotSheetTarget &&
        plan[mealSlotSheetTarget.dateKey]?.[mealSlotSheetTarget.mealType]?.name;
      if (a.name === currentMealName) return -1;
      if (b.name === currentMealName) return 1;

      if (!sortField || !sortDirection) return 0;
      const dir = sortDirection === "asc" ? 1 : -1;
      if (sortField === "calories") return ((a.calories || 0) - (b.calories || 0)) * dir;
      if (sortField === "time") return ((a.time || 0) - (b.time || 0)) * dir;
      if (sortField === "protein") return ((a.macros?.protein || 0) - (b.macros?.protein || 0)) * dir;
      return 0;
    })
            .map((recipe) => {
              const isCurrentMeal =
                mealSlotSheetTarget &&
                plan[mealSlotSheetTarget.dateKey]?.[mealSlotSheetTarget.mealType]
                  ?.name === recipe.name;

              return (
                <div
                  key={recipe.name}
                  onClick={() =>
                    setMealSlotSelectedRecipe((prev) =>
                      prev?.name === recipe.name ? null : recipe
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    padding: "12px 14px",
                    borderRadius: "18px",
                    marginBottom: "8px",
                    background:
                      mealSlotSelectedRecipe?.name === recipe.name
                        ? appTheme.accentLight
                        : isCurrentMeal
                        ? appTheme.softBackground
                        : "#ffffff",
                    border:
                      mealSlotSelectedRecipe?.name === recipe.name
                        ? appTheme.accentBorder
                        : isCurrentMeal
                        ? appTheme.pillBorder
                        : appTheme.cardBorder,
                    boxShadow: mealSlotSelectedRecipe?.name === recipe.name || isCurrentMeal
                      ? "none"
                      : "0 2px 8px rgba(0,0,0,0.06)",
                    cursor: "pointer",
                    transition: "all 0.18s ease"
                  }}
                >
                  <div
                    style={{
                      width: "52px",
                      height: "52px",
                      borderRadius: "14px",
                      overflow: "hidden",
                      flexShrink: 0,
                      background: recipe.image
                        ? `url(${recipe.image})`
                        : appTheme.accentLight,
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    }}
                  />

                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <div style={{
                        fontSize: "15px", fontWeight: "700",
                        color: appTheme.primaryText, marginBottom: "3px", lineHeight: 1.2
                      }}>
                        {recipe.name}
                      </div>
                      {favouriteRecipes.includes(recipe.name) && (
                        <span style={{ color: "#E6544C", fontSize: "13px", marginBottom: "3px" }}>♥</span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      <div style={{ fontSize: "12px", color: appTheme.secondaryText }}>
                        {recipe.time} min • {recipe.calories} kcal
                      </div>
                      {recipe.tags?.[0] && (
                        <span style={{
                          padding: "2px 8px",
                          borderRadius: "999px",
                          background: appTheme.accentColor,
                          color: "#ffffff",
                          fontSize: "10px",
                          fontWeight: "700",
                          letterSpacing: "0.01em",
                          whiteSpace: "nowrap",
                        }}>
                          {TAG_LABELS[recipe.tags[0]] || recipe.tags[0]}
                        </span>
                      )}
                    </div>
                  </div>

                  {mealSlotSelectedRecipe?.name === recipe.name && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: "6px",
                        flexShrink: 0
                      }}
                    >
                      <div
                        style={{
                          width: "22px",
                          height: "22px",
                          borderRadius: "999px",
                          background: appTheme.accentColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <svg
                          viewBox="0 0 12 12"
                          width="12"
                          height="12"
                          fill="none"
                          stroke="#ffffff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M2 6l3 3 5-5" />
                        </svg>
                      </div>
                      <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openRecipeDetails(recipe, true);
                          }}
                        style={{
                          padding: "5px 10px",
                          borderRadius: "999px",
                          border: appTheme.accentBorder,
                          background: appTheme.accentLight,
                          color: appTheme.accentColor,
                          fontSize: "11px",
                          fontWeight: "700",
                          cursor: "pointer",
                          whiteSpace: "nowrap"
                        }}
                      >
                        Details
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

          {filteredRecipes.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "30px 20px",
                color: appTheme.secondaryText,
                fontSize: "14px",
                lineHeight: 1.6
              }}
            >
              No recipes match your filters.
            </div>
          )}
        </div>

        {/* Confirm button */}
        {mealSlotSelectedRecipe && (
          <div
            style={{
              padding: "12px 18px",
              paddingBottom: isMobile ? "34px" : "18px",
              flexShrink: 0,
              display: "flex",
              justifyContent: "flex-end"
            }}
          >
            <button
              onClick={() => applyMealToSlot(mealSlotSelectedRecipe)}
              style={{
                padding: "12px 22px",
                borderRadius: "999px",
                border: appTheme.accentBorder,
                background: appTheme.accentColor,
                backdropFilter: "blur(12px) saturate(160%)",
                WebkitBackdropFilter: "blur(12px) saturate(160%)",
                color: "#ffffff",
                fontWeight: "700",
                fontSize: "15px",
                cursor: "pointer",
                boxShadow: "0 8px 24px rgba(34,197,94,0.25)",
                transition: "all 0.18s ease"
              }}
            >
              Confirm ✓
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MealSlotSheet;